import React, { useContext, useEffect, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import UserLoginContext from "../../../../context/userLoginContext";
import { useLocation, useNavigate } from "react-router-dom";
import { getName } from "../../../../utility/utility";
import { IAttendanceStatState } from "../types/attendance.types";
import { useBaseComponent } from "../../../../base/base-component";
import { getEmployeeSummary } from "../action/attendace.actions";
import AttendanceStatsView from "./attendance-stats.view";
import "../../../../styles/component/attendance-stats.scss";


const months = [
    {label: "All Months", value: "all"},
    {label: "January", value: "0"},
    {label: "February", value: "1"},
    {label: "March", value: "2"},
    {label: "April", value: "3"},
    {label: "May", value: "4"},
    {label: "June", value: "5"},
    {label: "July", value: "6"},
    {label: "August", value: "7"},
    {label: "September", value: "8"},
    {label: "October", value: "9"},
    {label: "November", value: "10"},
    {label: "December", value: "11"},
];

const AttendanceStats: React.FC = () => {
    const {user} = useContext(UserLoginContext);
    const location = useLocation();
    const {employeeId} = (location.state) || {};

    const navigate = useNavigate();




    const {state, updateState} = useBaseComponent<{}, IAttendanceStatState>({}, {
        initialState: () => ({
            data: {
                summary: null
            },
            internal: {
                filters: {
                    year: dayjs().year(),
                    month: String(dayjs().month()),
                    date: dayjs(),
                },
                empId: employeeId ?? user?.employeeId ?? "",
                empName: ''

            },
            isLoading: true
        }),

    });

    useEffect(() => {
        if (!employeeId) return;
        const empId = employeeId ?? user?.employeeId ?? "";
        const fetchData = async () => {
            updateState('isLoading', true);
            try {
                const res = await getEmployeeSummary(empId, state.internal.filters.year);
                const empName = getName(res.employeeName.firstName,
                    res.employeeName.middleName ?? '',
                    res.employeeName.lastName)
                updateState('internal', {
                    ...state.internal,
                    empName: empName,
                    empId: empId
                });
                updateState('data.summary', res)

            } catch (e) {
                console.log("Error while fetching summary", e)
            } finally {
                updateState('isLoading', false);
            }
        }

        fetchData();
    }, [state.internal.filters.year, employeeId]);


    function getWorkingMinutes(data: string): number {
        if (!data) return 0;

        const [hoursStr, minutesStr] = data.split(":");
        const hours = Number(hoursStr) || 0;
        const minutes = Number(minutesStr) || 0;

        return hours * 60 + minutes;
    }

    function minutesToHHMM(minutes: number): string {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}.${m.toString().padStart(2, "0")}`;
    }

    const monthSummary = useMemo(() => {
        if (!state.data.summary) return null;

        if (state.internal.filters.month === "all") {
            let totalHours: number = 0;
            const aggregated = state.data.summary.months.reduce(
                (acc, m) => {
                    acc.workingDays += m.workingDays;
                    acc.presentDays += m.presentDays;
                    acc.leaveDays += m.leaveDays;
                    acc.absentDays += m.absentDays;
                    acc.holidays += m.holidays;
                    acc.weekends += m.weekends;
                    totalHours += getWorkingMinutes(m.workingHours);
                    return acc;
                },
                {
                    month: "All Months",
                    workingDays: 0,
                    presentDays: 0,
                    leaveDays: 0,
                    absentDays: 0,
                    holidays: 0,
                    weekends: 0,
                    workingHours: "00:00",
                }
            );
            const workingHours = minutesToHHMM(totalHours);

            return {...aggregated, workingHours: workingHours};
        }

        const selectedMonthLabel = months.find(m => m.value === state.internal.filters.month)?.label.toLowerCase();
        return state.data.summary.months.find(m => m.month.toLowerCase() === selectedMonthLabel) ?? null;
    }, [state.data.summary, state.internal.filters.month]);


    const handleYearChange = (value: Dayjs | null) => {
        if (!value) return;
        updateState('internal.filters', {
            ...state.internal.filters,
            date: value,
            year: value.year(),
        })
    };


    const handleQuickMonth = (type: "curr" | "last") => {
        const date = type === "curr" ? dayjs() : dayjs().subtract(1, "month");
        updateState('internal.filters', {
            ...state.internal.filters,
            date,
            year: date.year(),
            month: String(date.month()),
        });
    };

    function handleMonthValueChange(value: string) {
        updateState('internal.filters', {
            ...state.internal.filters,
            month: value,
        });
    }

    function handleNavigationToRecord(empId: string) {
        if (user.isAdmin) {
            navigate('/employee/attendance', {state: {employeeId: empId}});
        } else if (user.employeeId) {
            navigate('/my/attendance', {state: {employeeId: empId}});
        }

    }

    return (
        <AttendanceStatsView
            handleQuickMonth={handleQuickMonth}
            handleYearChange={handleYearChange}
            handleMonthValueChange={handleMonthValueChange}
            monthSummary={monthSummary}
            filters={state.internal.filters}
            summary={state.data.summary}
            months={months}
            handleNavigationToRecord={handleNavigationToRecord}
            selectedEmployeeName={state.internal.empName}
            employeeId={state.internal.empId}
            isAdmin={user.isAdmin}
            isLoading={state.isLoading}
        />
    );
};

export default AttendanceStats;
